import { apiClients } from '../config/apiClient';

class DocumentConfigAPI {
  constructor() {
    this.client = apiClients.documentConfig;
  }

  // Métodos para tipos de documentos
  async getMyDocumentTypes() {
    try {
      const response = await this.client.get('/user/my-types');
      return response.data;
    } catch (error) {
      console.error('Error al obtener mis documentos:', error);
      throw new Error(error.response?.data?.detail || 'Error al obtener mis documentos');
    }
  }

  // Obtener plantillas públicas de administradores
  async getPublicTemplates() {
    try {
      const response = await this.client.get('/admin/templates');
      return response.data;
    } catch (error) {
      console.error('Error al obtener plantillas públicas:', error);
      throw new Error(error.response?.data?.detail || 'Error al obtener plantillas públicas');
    }
  }

  // Obtener plantillas públicas para usuarios regulares (importar)
  async getPublicTemplatesForUsers() {
    try {
      const response = await this.client.get('/public/templates');
      return response.data;
    } catch (error) {
      console.error('Error al obtener plantillas públicas:', error);
      throw new Error(error.response?.data?.detail || 'Error al obtener plantillas públicas');
    }
  }

  // Obtener tipos de documentos de un usuario específico (solo administradores)
  async getUserDocumentTypesAdmin(userId) {
    try {
      const response = await this.client.get(`/admin/user/${userId}/types`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener tipos de documentos del usuario:', error);
      throw new Error(error.response?.data?.detail || 'Error al obtener tipos de documentos del usuario');
    }
  }

  // Crear copia privada de una plantilla pública
  async createFromTemplate(templateId, newName = null) {
    try {
      const response = await this.client.post(`/user/from-template/${templateId}`, 
        newName ? { new_name: newName } : {}
      );
      return response.data;
    } catch (error) {
      console.error('Error al crear documento desde plantilla:', error);
      throw new Error(error.response?.data?.detail || 'Error al crear documento desde plantilla');
    }
  }

  async createDocumentType(documentType) {
    try {
      const response = await this.client.post('/types', documentType);
      return response.data;
    } catch (error) {
      console.error('Error al crear tipo de documento:', error);
      throw new Error(error.response?.data?.detail || 'Error al crear tipo de documento');
    }
  }

  async updateDocumentType(id, documentType) {
    try {
      const response = await this.client.put(`/types/${id}`, documentType);
      return response.data;
    } catch (error) {
      console.error('Error al actualizar tipo de documento:', error);
      throw new Error(error.response?.data?.detail || 'Error al actualizar tipo de documento');
    }
  }

  async deleteDocumentType(id) {
    try {
      const response = await this.client.delete(`/types/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error al eliminar tipo de documento:', error);
      throw new Error(error.response?.data?.detail || 'Error al eliminar tipo de documento');
    }
  }

  // Métodos auxiliares que detectan automáticamente el rol del usuario
  
  // Método para obtener campos según el rol del usuario
  async getFieldsByRole(documentTypeId, userRole) {
    if (userRole === 'admin') {
      try {
        const response = await this.client.get(`/types/${documentTypeId}/fields`);
        return response.data;
      } catch (error) {
        console.error('Error al obtener campos:', error);
        throw new Error(error.response?.data?.detail || 'Error al obtener campos');
      }
    } else {
      try {
        const response = await this.client.get(`/user/types/${documentTypeId}/fields`);
        return response.data;
      } catch (error) {
        console.error('Error al obtener campos:', error);
        throw new Error(error.response?.data?.detail || 'Error al obtener campos');
      }
    }
  }

  // Método para crear campos según el rol del usuario  
  async createFieldByRole(documentTypeId, field, userRole) {
    if (userRole === 'admin') {
      try {
        const response = await this.client.post(`/types/${documentTypeId}/fields`, field);
        return response.data;
      } catch (error) {
        console.error('Error al crear campo:', error);
        throw new Error(error.response?.data?.detail || 'Error al crear campo');
      }
    } else {
      try {
        const response = await this.client.post(`/user/types/${documentTypeId}/fields`, field);
        return response.data;
      } catch (error) {
        console.error('Error al crear campo:', error);
        let errorMessage = 'Error al crear campo';
        
        if (error.response?.data) {
          if (typeof error.response.data === 'string') {
            errorMessage = error.response.data;
          } else if (error.response.data.detail) {
            if (Array.isArray(error.response.data.detail)) {
              errorMessage = error.response.data.detail.map(err => err.msg).join(', ');
            } else {
              errorMessage = error.response.data.detail;
            }
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          }
        }
        
        throw new Error(errorMessage);
      }
    }
  }

  // Método para actualizar campos según el rol del usuario
  async updateFieldByRole(fieldId, field, userRole) {
    if (userRole === 'admin') {
      try {
        const response = await this.client.put(`/fields/${fieldId}`, field);
        return response.data;
      } catch (error) {
        console.error('Error al actualizar campo:', error);
        throw new Error(error.response?.data?.detail || 'Error al actualizar campo');
      }
    } else {
      try {
        const response = await this.client.put(`/user/fields/${fieldId}`, field);
        return response.data;
      } catch (error) {
        console.error('Error al actualizar campo:', error);
        throw new Error(error.response?.data?.detail || 'Error al actualizar campo');
      }
    }
  }

  // Método para eliminar campos según el rol del usuario
  async deleteFieldByRole(fieldId, userRole) {
    if (userRole === 'admin') {
      try {
        const response = await this.client.delete(`/fields/${fieldId}`);
        return response.data;
      } catch (error) {
        console.error('Error al eliminar campo:', error);
        throw new Error(error.response?.data?.detail || 'Error al eliminar campo');
      }
    } else {
      try {
        const response = await this.client.delete(`/user/fields/${fieldId}`);
        return response.data;
      } catch (error) {
        console.error('Error al eliminar campo:', error);
        throw new Error(error.response?.data?.detail || 'Error al eliminar campo');
      }
    }
  }

  // Método para inicializar datos por defecto
  async initializeDefaultData() {
    try {
      const response = await this.client.post('/initialize-default-data');
      return response.data;
    } catch (error) {
      console.error('Error al inicializar datos por defecto:', error);
      throw new Error(error.response?.data?.detail || 'Error al inicializar datos por defecto');
    }
  }
}

export const documentConfigAPI = new DocumentConfigAPI();
export default DocumentConfigAPI;
